import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api';
import { useApiMutationQuery } from '@shared/lib/mutations';
import { useCachedQuery } from '@shared/lib/query-helpers';

import { adminSyncWorkerSettingsQuery, adminSyncWorkerSettingsKeys } from './queries';
import {
  syncWorkerSettingsResponseSchema,
  syncWorkerSlackReportResponseSchema,
  type SyncWorkerSettingsResponse,
  type SyncWorkerSettingsValue,
  type SyncWorkerSlackReportResponse,
} from './schemas';

export const INTERNAL_MEDIA_TUNING_FIELDS = [
  'mediaImportLargeConcurrency',
  'mediaImportClaimBatchSize',
  'mediaImportLargeThresholdBytes',
  'mediaImportDbPressureBreakerEnabled',
  'mediaImportDbPressureThresholdMs',
  'mediaImportDbPressureCooldownSeconds',
  'mediaImportDegradedSmallConcurrency',
  'mediaImportDegradedLargeConcurrency',
  'mediaImportPreflightEnabled',
  'mediaImportPreflightConcurrency',
  'mediaImportPreflightTimeoutMs',
  'mediaOwnerSyncClaimBatchSize',
  'mediaOwnerSyncProcessConcurrency',
  'mediaOwnerSyncMaxActive',
  'mediaOwnerSyncTickTimeBudgetMs',
  'mediaOwnerSyncDbPressureBreakerEnabled',
  'mediaOwnerSyncClaimDbPressureThresholdMs',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

export const LEGACY_MIRROR_DELTA_FIELDS = [
  'mirrorDeltaPageSize',
  'mirrorDeltaBatchSize',
  'mirrorDeltaWorkBatchSize',
  'mirrorDeltaPollIntervalSeconds',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

export const DERIVED_POSTPROCESS_FIELDS = [
  'standardPostprocessMicrobatchSize',
  'heavyRelationPostprocessMicrobatchSize',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

export const INTERNAL_DELETE_LANE_SAFEGUARD_FIELDS = [
  'deleteBacklogLeaseMs',
  'deleteBacklogBaseBackoffSeconds',
  'deleteBacklogMaxBackoffSeconds',
  'deleteVerificationTimeoutMs',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

export const INTERNAL_RELATION_REFRESH_SAFEGUARD_FIELDS = [
  'relationRefreshLockTimeoutMs',
  'relationRefreshStatementTimeoutMs',
  'relationRefreshIdleInTxTimeoutMs',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

export const INTERNAL_SNAPSHOT_LANE_SAFEGUARD_FIELDS = [
  'relationSnapshotExactRecountStatementTimeoutMs',
  'snapshotLeaseWindowSeconds',
  'snapshotHeartbeatIntervalSeconds',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

export const INTERNAL_DATA_VALIDATION_FIELDS = [
  'dataValidationReportRetentionDays',
  'dataValidationFullScanEnabled',
  'dataValidationLeaseMs',
  'dataValidationCleanupBatchSize',
  'dataValidationMaxReportBytes',
  'dataValidationSnapshotRetentionDays',
  'dataValidationDirtySnapshotRetentionDays',
  'dataValidationRawReportRetentionDays',
  'dataValidationFinalReportRetentionDays',
  'dataValidationFreshnessDays',
] as const satisfies ReadonlyArray<keyof SyncWorkerSettingsValue>;

type InternalMediaTuningField = (typeof INTERNAL_MEDIA_TUNING_FIELDS)[number];
type LegacyMirrorDeltaField = (typeof LEGACY_MIRROR_DELTA_FIELDS)[number];
type DerivedPostprocessField = (typeof DERIVED_POSTPROCESS_FIELDS)[number];
type InternalDeleteLaneSafeguardField = (typeof INTERNAL_DELETE_LANE_SAFEGUARD_FIELDS)[number];
type InternalRelationRefreshSafeguardField =
  (typeof INTERNAL_RELATION_REFRESH_SAFEGUARD_FIELDS)[number];
type InternalSnapshotLaneSafeguardField = (typeof INTERNAL_SNAPSHOT_LANE_SAFEGUARD_FIELDS)[number];
type InternalDataValidationField = (typeof INTERNAL_DATA_VALIDATION_FIELDS)[number];

export type SyncWorkerSettingsUpdatePayload = Partial<
  Omit<
    SyncWorkerSettingsValue,
    | InternalMediaTuningField
    | LegacyMirrorDeltaField
    | DerivedPostprocessField
    | InternalDeleteLaneSafeguardField
    | InternalRelationRefreshSafeguardField
    | InternalSnapshotLaneSafeguardField
    | InternalDataValidationField
  >
>;
export function useAdminSyncWorkerSettings() {
  const query = useMemo(() => adminSyncWorkerSettingsQuery(), []);
  return useCachedQuery(query);
}

export function useUpdateAdminSyncWorkerSettings() {
  return useApiMutationQuery<SyncWorkerSettingsResponse, SyncWorkerSettingsUpdatePayload>({
    path: '/api/v1/admin/sync-worker-settings',
    method: 'PATCH',
    schema: syncWorkerSettingsResponseSchema,
    invalidateKeys: [adminSyncWorkerSettingsKeys.all],
  });
}

export function useSendAdminSyncWorkerSlackReport() {
  const queryClient = useQueryClient();

  return useMutation<SyncWorkerSlackReportResponse>({
    mutationFn: async () =>
      await api.post(
        '/api/v1/admin/sync-worker-settings/slack-report',
        {},
        {
          headers: { 'Idempotency-Key': crypto.randomUUID() },
          schema: syncWorkerSlackReportResponseSchema,
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminSyncWorkerSettingsKeys.all });
    },
  });
}
